import Details from '@/components/Details/Details';
import InfoLabel from '@/components/InfoLabel';
import IEventType, { IServiceLayout, IServiceTheme } from '@/types/IEventType';
import { IEventTypeWrite } from '@/types/apiClient/IEventTypeWrite';
import {
	Avatar,
	Button,
	Divider,
	FormControl,
	IconButton,
	Radio,
	RadioGroup,
	Stack,
	VStack
} from '@chakra-ui/react';
import { FcEditImage } from 'react-icons/fc';
import { MdDelete } from 'react-icons/md';
import useSectionAppearance from './useSectionAppearance';

export interface ISectionCalendarsProps {
	isLoading: boolean;
	service: IEventType;
	onChange: (values: Partial<IEventTypeWrite>) => void;
}

export default function SectionAppearance(props: ISectionCalendarsProps) {
	const { handleChangeImageUrlClick, handleDeleteImageClick, onSubmit, t, form } = useSectionAppearance(props);

	return (
		<Details image={<FcEditImage />} title={t('Appearance')}>
			<FormControl>
				<InfoLabel
					text={t('Booking page image')}
					help={t('Select an image to display on your booking page.')}
					htmlFor="page-image"
				/>
				<VStack>
					<Avatar size="xl" src={form.getValues('avatar')}>
						{form.getValues('avatar') ? (
							<IconButton
								aria-label={t('Delete')}
								borderRadius="50%"
								colorScheme="red"
								icon={<MdDelete />}
								isLoading={props.isLoading}
								onClick={handleDeleteImageClick}
								position="absolute"
								right="-2"
								size="sm"
								top="0"
							/>
						) : null}
					</Avatar>
					<Button isLoading={props.isLoading} type="button" size="sm" onClick={handleChangeImageUrlClick}>
						{t('Change image')}
					</Button>
				</VStack>
			</FormControl>

			<Divider mt={4} mb={3} />

			<FormControl>
				<InfoLabel help="Select an color schema to display on page." htmlFor="color" text={t('Color scheme')} />
				<RadioGroup
					id="color"
					isDisabled={props.isLoading}
					name="color"
					value={form.getValues('color')}
					onChange={(v: IServiceTheme) => {
						form.setValue('color', v);
						onSubmit();
					}}
					mt={2}
				>
					<Stack spacing={5} direction="row">
						<Radio size="lg" bg="primary.400" value="primary" colorScheme="primary" />
						<Radio size="lg" bg="teal.400" value="teal" colorScheme="teal" />
						<Radio size="lg" bg="blue.400" value="blue" colorScheme="blue" />
						<Radio size="lg" bg="green.400" value="green" colorScheme="green" />
						<Radio size="lg" bg="cyan.400" value="cyan" colorScheme="cyan" />
						<Radio size="lg" bg="purple.400" value="purple" colorScheme="purple" />
						<Radio size="lg" bg="gray.400" value="gray" colorScheme="gray" />
						<Radio size="lg" bg="facebook.400" value="facebook" colorScheme="facebook" />
						<Radio size="lg" bg="black.900" value="black" colorScheme="black" />
					</Stack>
				</RadioGroup>
			</FormControl>

			<Divider mt={4} mb={3} />

			<FormControl isInvalid={false}>
				<InfoLabel htmlFor="layout" text={t('Layout')} help={t('Choose between a weekly or a monthly view')} />
				<RadioGroup
					id="layout"
					isDisabled={props.isLoading}
					name="layout"
					value={form.getValues('layout')}
					onChange={(v: IServiceLayout) => {
						form.setValue('layout', v);
						onSubmit();
					}}
					mt={2}
				>
					<Stack spacing={5} direction="row">
						<Radio value="week">{t('Week')}</Radio>
						<Radio value="month">{t('Month')}</Radio>
					</Stack>
				</RadioGroup>
			</FormControl>
		</Details>
	);
}
